import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import _ from 'lodash'

import { PopoverContextProvider } from '@/context'
import { ConditionalWrapper } from '../Wrapper'

import { IPopoverProps, ITriggerAction } from './types'
import { SlideInOut } from './slideInOut'
import Trigger from './trigger'
import Content from './content'
import './styles.scss'

export const Popover = forwardRef<ITriggerAction, IPopoverProps>(
  (
    {
      className,
      disabled,
      initialOpen,
      button,
      placement,
      autoScroll = false,
      positioning = 'absolute',
      hasArrow = true,
      closeOnOutside = true,
      distance = 0,
      trigger = 'on-click',
      WrapEl = 'div',
      onOpened,
      onClosed,
      children,
    },
    ref,
  ) => {
    const elementRef = useRef<HTMLElement>(null)
    const [open, setOpen] = useState<boolean | undefined>()
    const [contentVisible, setContentVisibility] = useState<
      boolean | undefined
    >()
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

    useEffect(() => {
      if (initialOpen !== undefined) {
        setOpen(initialOpen)
      }
    }, [initialOpen])

    const onScroll = useCallback(
      (block: 'start' | 'end') => {
        if (!autoScroll) return
        clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => {
          elementRef.current?.scrollIntoView({
            block,
            behavior: 'smooth',
          })
        }, 200)
      },
      [autoScroll],
    )

    useEffect(() => {
      if (open === undefined) return

      if (open) {
        onOpened?.()
        onScroll('start')
      } else {
        onClosed?.()
        onScroll('end')
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, onScroll])

    const showPopover = useCallback(() => {
      if (open) return
      setContentVisibility(true)
      _.delay(setOpen, 50, true)
    }, [open])

    const hidePopover = useCallback(() => {
      setContentVisibility(false)
      _.delay(setOpen, 200, false)
    }, [])

    return (
      <PopoverContextProvider
        closeOnOutside={closeOnOutside}
        onClose={hidePopover}
        placement={placement}
        distance={distance}
      >
        <ConditionalWrapper
          ref={elementRef}
          className={classNames('popover-wrapper', className, { open: !!open })}
          WrapEl={WrapEl}
          {...(WrapEl
            ? {
                onMouseLeave: () =>
                  trigger === 'mouse-over' ? hidePopover() : null,
              }
            : {})}
        >
          <Trigger
            ref={ref}
            trigger={trigger}
            disabled={!!disabled}
            onClose={hidePopover}
            onTrigger={showPopover}
          >
            {button}
          </Trigger>

          {open && (
            <Content
              className={classNames({ visible: contentVisible })}
              positioning={positioning}
            >
              <SlideInOut
                show={open && contentVisible}
                duration={200}
                placement={placement}
              >
                {hasArrow && <div className="arrow-container" />}
                {typeof children === 'function'
                  ? children(hidePopover)
                  : children}
              </SlideInOut>
            </Content>
          )}
        </ConditionalWrapper>
      </PopoverContextProvider>
    )
  },
)

export type IPopover = React.ElementRef<typeof Popover> | null
